<template>
  <div class="wrapper">
    <data-filter :on-apply-filter="onApplyFilter" hide-report download-excel :on-download-excel="onDownloadExcel"
      :minute=true />

    <!-- start unit selection -->
    <div class="wrapper mt-2">
      <div class="d-flex align-items-center">
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">Speed Unit</span>
          <select name="" class="form-control" v-model="unit.speed">
            <option value="knot">KNOT</option>
            <option value="km/h">KM/H</option>
          </select>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">Distance Unit</span>
          <select name="" class="form-control" v-model="unit.distance">
            <option value="nm">NM</option>
            <option value="km">KM</option>
          </select>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">Sort Data By</span>
          <select name="" class="form-control" v-model="dataSort">
            <option value="latest">Latest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">&nbsp;</span>
          <button class="btn btn-mantraz" @click="showShowHideModal">
            Filter Column
          </button>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">&nbsp;</span>
          <button class="btn btn-mantraz" @click="resetColumns">
            Load Default Column
          </button>
        </div>
        <div class="ml-3" v-if="isSuperAdmin">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">Anomaly Highlight</span>
          <button class="btn" :class="highlightAnomaly ? 'btn-mantraz' : 'btn-outline-mantraz'"
            @click="highlightAnomaly = !highlightAnomaly">
            {{ highlightAnomaly ? 'On' : 'Off' }}
          </button>
        </div>
      </div>
    </div>
    <!-- end unit selection -->

    <template v-if="isLoading">
      <div class="text-center mt-5">
        <b-spinner label="Spinning"></b-spinner>
      </div>
    </template>
    <template v-else-if="currAssetSummary && !isLoading">
      <div class="iq-card mt-3">
        <div class="iq-card-body pt-2 pb-5 mb-5">
          <datalog-table
            ref="datalogTable"
            :asset="currAsset"
            :series="fullSeries"
            :interval="appliedInterval || interval"
            :unit="unit"
            :highlight-anomaly="isSuperAdmin && highlightAnomaly"
            :export-file-name="exportFileName" />
          <hr />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/* eslint-disable object-curly-newline */
import moment from 'moment';
import { mapState } from 'vuex';
import DataFilter from '@/components/DataFilter';
import DatalogTable from '@/components/datalog/DatalogTable';
import { formatDateTime } from '@/util/date';
import { buildFmsSeries } from '@/util/fms-summary';
import { initAssetSummary, applyAssetSummaryFilter } from '@/util/asset-summary-loader';

export default {
  name: 'FmsReportDatalog',
  components: {
    DataFilter,
    DatalogTable,
  },
  data() {
    return {
      unit: {
        distance: 'nm',
        speed: 'knot',
      },
      dataSort: 'oldest',
      masks: {
        input: 'DD-MM-YYYY HH:mm',

      },
      isLoading: true,
      appliedDateRange: null,
      appliedInterval: null,
      appliedMassId: null,
      highlightAnomaly: false, // Toggle highlight baris anomali RPM vs FLOW (super admin), default off
    };
  },

  computed: {

    currAsset() {
      const currMassId = this.appliedMassId || this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) return null;
      return assets.find((asset) => asset.massId === currMassId);
    },
    currAssetSummary() {
      if (!this.currAsset?.summary) return null;
      const effectiveDateRange = this.appliedDateRange || this.dateRange;
      const effectiveInterval = this.appliedInterval || this.interval;

      return {
        series: buildFmsSeries({
          summary: this.currAsset.summary,
          dateRange: effectiveDateRange,
          interval: effectiveInterval,
          roundingStrategy: 'interval',
          normalizeHourlyTimestamps: effectiveInterval === 'hour',
          dataSort: this.dataSort,
        }),
      };
    },
    fullSeries() {
      return this.currAssetSummary?.series || [];
    },
    exportFileName() {
      const range = this.appliedDateRange || this.dateRange;
      const cap = (v) => formatDateTime(v, 'DD-MM-YYYY HH:mm', null, false);
      return `${this.currAsset?.massName || 'asset'} - ${cap(range.start)} - ${cap(range.end)}`;
    },
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());
        return { start: startDate, end: endDate };
      },
      set(val) {
        const start = moment(val.start);
        const end = moment(val.end);
        this.$store.commit('filter/SET_DATE', { start, end });
      },
    },
    ...mapState('asset', ['assets']),
    ...mapState('filter', {
      currentMassId: (state) => state.currentMassId,
      interval: (state) => state.interval,
      rangeType: (state) => state.rangeType,
    }),
    selectedInterval: {
      get() {
        return this.interval;
      },
      set(val) {
        this.$store.dispatch('filter/setInterval', val);
      },
    },
    selectedAsset: {
      get() {
        return this.currentMassId;
      },
      set(val) {
        this.$store.dispatch('filter/setCurrentMassId', val);
      },
    },
    selectedRangeType: {
      get() {
        return this.rangeType;
      },
      set(val) {
        this.$store.dispatch('filter/setRangeType', val);
      },
    },
  },

  watch: {
    currentMassId: {
      handler(newMassId, oldMassId) {
        // Ganti aset: tabel rebuild kolomnya sendiri lewat computed `capabilitySignature`.
        if (newMassId && newMassId !== oldMassId) {
          this.appliedMassId = newMassId;
        }
      },
    },
  },

  methods: {
    moment(date) {
      return moment(date);
    },

    async warnFailedSeries(seriesPromise) {
      if (!seriesPromise) return;
      const failed = await seriesPromise;
      if (failed?.length) {
        this.$bvToast.toast(`Time series unavailable for: ${failed.join(', ')}`, {
          title: 'Incomplete data',
          variant: 'warning',
          solid: true,
        });
      }
    },

    async onApplyFilter() {
      this.isLoading = true;
      try {
        const result = await applyAssetSummaryFilter(this.$store, {
          dateRange: this.dateRange,
          interval: this.selectedInterval || this.interval,
          massId: this.selectedAsset || this.currentMassId,
          summarySource: 'rollup',
          withSeries: true,
          seriesVariant: 'datalog',
        });
        await this.warnFailedSeries(result.seriesPromise);
        this.appliedDateRange = {
          start: new Date(moment(result.dateRange.start).valueOf()),
          end: new Date(moment(result.dateRange.end).valueOf()),
        };
        this.appliedInterval = result.interval;
        this.appliedMassId = result.massId;
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },

    showShowHideModal() {
      // Tabelnya belum ada selama loading, jadi dijaga.
      if (this.$refs.datalogTable) this.$refs.datalogTable.showColumnModal();
    },

    resetColumns() {
      if (this.$refs.datalogTable) this.$refs.datalogTable.resetColumns();
    },

    async onDownloadExcel() {
      await this.$refs.datalogTable?.downloadExcel();
    },
  },
  async mounted() {
    const date = this.$store.getters['filter/date'];
    const initResult = await initAssetSummary(this.$store, {
      dateRange: date,
      withDevices: true,
      withLastData: true,
      summarySource: 'rollup',
      withSeries: true,
      seriesVariant: 'datalog',
    });
    // Beda dari halaman chart: di sini deret waktu ITU isi tabelnya, jadi
    // ditungguin. Kalau enggak, tabel kosong dulu baru keisi.
    await this.warnFailedSeries(initResult.seriesPromise);

    this.appliedDateRange = {
      start: new Date(moment(date.start).valueOf()),
      end: new Date(moment(date.end).valueOf()),
    };

    this.appliedInterval = this.$store.getters['filter/interval'];
    this.appliedMassId = this.$store.getters['filter/currentMassId'];

    this.isLoading = false;
  },
};

</script>

<style scoped>
.flex-container {
  display: flex;
  justify-content: space-between;
}

.flex-item {
  /* padding: 10px 20px; */
  margin: 0 5px;
}

@media screen and (max-width: 600px) {
  .flex-container {
    flex-direction: column;
  }

  .flex-item {
    width: 100%;
  }
}
</style>
